import type GlobalStore from "@/store";
import type { ModalStore } from "@/hooks/Modal/ModalStore";
import OrderDTO from "@/dto/order/OrderDTO";
import { ShippingWay } from "@/services/api/orderShip";
import ShipValidateErrorModal from "./ShipValidateErrorModal";
import LogisticsCompany from "@/enum/LogisticsCompany";
import { validateShipping } from "@/services/ship";
import { ShipValidationRuleKey } from "@/services/ship/type";
import ReshipValidateErrorModal from "../../dialogs/ReshipOrderDialog/ReshipValidateErrorModal";
import { SubmitBatchShipParams } from "../../dialogs/ShipDialog/useShip";
import { getReshipValidator } from "../../dialogs/ReshipOrderDialog/useReship";
import { getWaybillValidator, ShipWaybillValidateErrorData } from "./getWaybillValidator";

// ================ 参数类型定义 ================

interface CommonShipParams {
    selectedOrders: OrderDTO[];
    globalStore: typeof GlobalStore;
    shipOrder(params: SubmitBatchShipParams): Promise<void>;
}

interface OfflineShipParams extends CommonShipParams {
    modalStore: ModalStore;
    allOrders: OrderDTO[];
    updateOrder?(orderId: number, params: Partial<OrderDTO>): any;
}

interface OnlineShipParams extends CommonShipParams {
    modalStore: ModalStore;
    allOrders: OrderDTO[];
    updateOrder?(orderId: number, params: Partial<OrderDTO>): any;
}

interface VirtualShipParams extends CommonShipParams {}

interface ReshipShipParams extends Omit<CommonShipParams, "shipOrder"> {
    modalStore: ModalStore;
    allOrders: OrderDTO[];
    updateOrder?(orderId: number, params: Partial<OrderDTO>): any;
    reshipOrder(orders: OrderDTO[]): Promise<void>;
}

interface JdSellerDeliveryShipParams extends CommonShipParams {
    onShipTaskDone(): Promise<void>;
}

// ================ 工具函数 ================

/**
 *（自己联系物流|在线下单）运单号校验失败处理逻辑
 */

function getWaybillValidationHandler(updateOrder: OfflineShipParams["updateOrder"], modalStore: ModalStore) {
    return (errors: ShipWaybillValidateErrorData[], validOrders: OrderDTO[]) => {
        errors.forEach((error) => {
            if (updateOrder) {
                updateOrder(error.orderId, {
                    waybillNoInputValidateErrorMsg: error.errorMessage,
                    waybillNoInputValidationActived: true,
                });
            }
        });

        return new Promise<boolean>((resolve, reject) => {
            modalStore.openModal(ShipValidateErrorModal, {
                dataSource: errors,
                canShipOrders: validOrders,
                onContinue: () => resolve(true),
                onAbort: () => reject(false),
            });
        });
    };
}

/**
 * 重新发货运单号校验失败处理逻辑
 */

function getReshipWaybillValidationHandler(updateOrder: ReshipShipParams["updateOrder"], modalStore: ModalStore) {
    return (errors: ShipWaybillValidateErrorData[], validOrders: OrderDTO[]) => {
        errors.forEach((error) => {
            if (updateOrder) {
                updateOrder(error.orderId, {
                    waybillNoInputValidateErrorMsg: error.errorMessage,
                    waybillNoInputValidationActived: true,
                });
            }
        });

        return new Promise<boolean>((resolve, reject) => {
            modalStore.openModal(ReshipValidateErrorModal, {
                dataSource: errors,
                canShipOrders: validOrders,
                onContinue: () => resolve(true),
                onAbort: () => reject(false),
            });
        });
    };
}

// ================ 订单列表 & 快速查单 发货按钮 处理函数 ================
// 使用预设的规则执行发货
// 适用场景：
//   √ 订单管理页面的[发货]按钮
//   √ 快速查单页面的[发货]按钮
//
// 不适用场景说明：
//   × 打印后发货 - 因打印前已触发部分规则验证流程
//   × 带业务逻辑的发货场景：
//      1. 扫描打印发货
//      2. 扫描发货
//      3. 拿货标签发货
// ========================================================

// 自己联系物流发货
export async function execOfflineShip(params: OfflineShipParams) {
    const { modalStore, updateOrder, selectedOrders, globalStore, shipOrder, allOrders } = params;
    const waybillValidationHandler = getWaybillValidationHandler(updateOrder, modalStore);

    const { canShip, canShipOrders } = await validateShipping({
        globalStore,
        orders: selectedOrders,
        rule: {
            [ShipValidationRuleKey.checkDisabledPrintShipping]: true,
            [ShipValidationRuleKey.checkAutoPrint]: true,
            [ShipValidationRuleKey.checkRefundingOrder]: true,
            [ShipValidationRuleKey.checkJdMaxEwaybill]: true,
            [ShipValidationRuleKey.checkOrderItemPartQuantityShip]: true,
            [ShipValidationRuleKey.checkCannotShipTag]: true,
            [ShipValidationRuleKey.checkManualOrderWaybillNo]: true,
        },
        validators: [getWaybillValidator({ onWaybillError: waybillValidationHandler, allOrders })],
    });

    if (!canShip || canShipOrders.length === 0) {
        return;
    }

    await shipOrder({
        orders: canShipOrders,
        shippingWay: ShippingWay.OFFLINE,
    });
}

// 发货（在线下单）
export async function execOnlineShip(params: OnlineShipParams) {
    const { modalStore, selectedOrders, updateOrder, globalStore, shipOrder, allOrders } = params;
    const waybillValidationHandler = getWaybillValidationHandler(updateOrder, modalStore);

    const { canShip, canShipOrders } = await validateShipping({
        globalStore,
        orders: selectedOrders,
        rule: {
            [ShipValidationRuleKey.checkDisabledPrintShipping]: true,
            [ShipValidationRuleKey.checkAutoPrint]: true,
            [ShipValidationRuleKey.checkRefundingOrder]: true,
            [ShipValidationRuleKey.checkJdMaxEwaybill]: true,
            [ShipValidationRuleKey.checkOrderItemPartQuantityShip]: true,
            [ShipValidationRuleKey.checkCannotShipTag]: true,
        },
        validators: [getWaybillValidator({ onWaybillError: waybillValidationHandler, allOrders })],
    });

    if (!canShip || canShipOrders.length === 0) {
        return;
    }

    await shipOrder({
        orders: canShipOrders,
        shippingWay: ShippingWay.ONLINE,
    });
}

// 发货（无需物流）
export async function execVirtualShip(params: VirtualShipParams) {
    const { globalStore, selectedOrders, shipOrder } = params;
    const { canShip, canShipOrders } = await validateShipping({
        globalStore,
        orders: selectedOrders,
        rule: {
            [ShipValidationRuleKey.checkDisabledPrintShipping]: true,
            [ShipValidationRuleKey.checkRefundingOrder]: true,
            [ShipValidationRuleKey.checkJdMaxEwaybill]: true,
            [ShipValidationRuleKey.checkOrderItemPartQuantityShip]: true,
            [ShipValidationRuleKey.checkCannotShipTag]: true,
            [ShipValidationRuleKey.checkVirtualShip]: true,
        },
    });

    if (!canShip || canShipOrders.length === 0) {
        return;
    }

    await shipOrder({
        orders: canShipOrders,
        shippingWay: ShippingWay.VIRTUAL,
    });
}

export async function execJdSellerDelivery(params: JdSellerDeliveryShipParams) {
    const { globalStore, selectedOrders, shipOrder } = params;
    const { canShip, canShipOrders } = await validateShipping({
        globalStore,
        orders: selectedOrders,
        rule: {
            [ShipValidationRuleKey.checkRefundingOrder]: true,
        },
    });

    if (!canShip || canShipOrders.length === 0) {
        return;
    }

    await shipOrder({
        orders: canShipOrders,
        shippingWay: ShippingWay.OFFLINE,
        logisticsCompany: LogisticsCompany.JD_SELLER_DELIVERY,
    });
}

// ================ 重新发货处理函数 ================
export async function execReshipOrder(params: ReshipShipParams) {
    const { selectedOrders, modalStore, globalStore, allOrders, updateOrder, reshipOrder } = params;
    const waybillValidationHandler = getReshipWaybillValidationHandler(updateOrder, modalStore);

    const { canShip, canShipOrders } = await validateShipping({
        globalStore,
        orders: selectedOrders,
        validators: [
            getWaybillValidator({ allOrders, onWaybillError: waybillValidationHandler }),
            getReshipValidator(modalStore),
        ],
    });

    if (!canShip || canShipOrders.length === 0) return;

    await reshipOrder(canShipOrders);
}
